import { JitsiMeeting } from "@jitsi/react-sdk";
import { Close } from "@mui/icons-material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import VideocamIcon from "@mui/icons-material/Videocam";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { VITE_JITSI_URL } from "../../../config";
import { setIsCallJoined } from "../../../redux/slices/module.slice";
import { setOccurenceDate } from "../../../redux/slices/ehr/visitnote.slice";
import type { RootState } from "../../../redux/store/store";
import { useJitsi } from "../../contexts/VideoCallContext";
import { fetchJitsiToken } from "../../utils/helper";
import { theme } from "../../utils/theme";
import CallEndedScreen from "./CallEndedScreen";
import { jitsiConfig, jitsiInterfaceConfig } from "./config";
import FullScreenCallEndedScreen from "./FullScreenCallEndedScreen";
import Invite from "./overlayComponents/Invite";

const SharedVideoCall = () => {
  const {
    isOpen,
    isMinimized,
    callState,
    callLink,
    roomName,
    appointmentUuid,
    occurrenceDate,
    displayName,
    endCall,
    minimizeJitsi,
    maximizeJitsi,
    setCallActive,
    closeJitsi,
  } = useJitsi();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((store: RootState) => store?.auth);
  const { isCallJoined } = useSelector((state: RootState) => state.module);
  const isDashboard = location.pathname === "/dashboard";
  const [prevPath, setPrevPath] = useState(location.pathname);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [position, setPosition] = useState({
    x: window.innerWidth - 340,
    y: window.innerHeight - 240,
  });
  const [size, setSize] = useState({ width: 320, height: 420 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [jitsiJwt, setJitsiJwt] = useState<string | undefined>(undefined);

  // Fetch a signed Jitsi JWT (moderator for providers) whenever a call opens.
  // The bridge now requires this HS256 token; the raw access_token is rejected.
  useEffect(() => {
    if (!isOpen) {
      setJitsiJwt(undefined);
      return;
    }
    const room = appointmentUuid || roomName;
    let cancelled = false;
    fetchJitsiToken(room).then((token) => {
      if (!cancelled) setJitsiJwt(token);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, appointmentUuid, roomName]);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (callState === "joining") {
      const timer = setTimeout(() => {
        setCallActive();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [callState, setCallActive]);

  const constrainPosition = (x: number, y: number) => {
    const navbarHeight = isDashboard ? 0 : 58;
    const boundedX = Math.max(
      20,
      Math.min(window.innerWidth - size.width - 20, x),
    );
    const boundedY = Math.max(
      navbarHeight + 20,
      Math.min(window.innerHeight - size.height - 20, y),
    );
    return { x: boundedX, y: boundedY };
  };

  useEffect(() => {
    if (isMinimized) {
      setPosition((prev) => constrainPosition(prev.x, prev.y));
    }

    if (location.pathname !== prevPath && !isMinimized && isOpen) {
      minimizeJitsi();
    }

    setPrevPath(location.pathname);
  }, [location.pathname, isMinimized, minimizeJitsi, isOpen]);

  const handleDragStart = (e: React.MouseEvent) => {
    if (isResizing || !isMinimized) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    if (!isMinimized) return;
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
    });

    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && isMinimized) {
        const newPosition = constrainPosition(
          e.clientX - dragStart.x,
          e.clientY - dragStart.y,
        );
        setPosition(newPosition);
      } else if (isResizing && isMinimized) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        const newWidth = Math.max(
          280,
          Math.min(800, resizeStart.width + deltaX),
        );
        const newHeight = Math.max(
          180,
          Math.min(600, resizeStart.height + deltaY),
        );
        setSize({ width: newWidth, height: newHeight });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      document.body.style.userSelect = "";
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart, resizeStart, size, isMinimized]);

  if (!isOpen) return null;

  const url = new URL(callLink);

  const userInfo = {
    displayName:
      [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
      "Participant",
    email: user?.email || "user@example.com",
  };

  const showCallEndedScreen = callState === "ended";
  const showActiveCall = callState === "joining" || callState === "active";
  const isVisitNotePage = location.pathname.includes(
    "/ehr/scheduling/visit-note/",
  );
  const currentpath = location.pathname;

  if (showCallEndedScreen) {
    if (isVisitNotePage) {
      return (
        <Box
          sx={{
            position: "fixed",
            top: position.y,
            left: position.x,
            width: size.width,
            height: size.height,
            zIndex: 9999,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow:
              "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
          }}
        >
          <IconButton
            onClick={closeJitsi}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "white",
              padding: "4px",
              zIndex: 10000,
              "&:hover": {
                backgroundColor: "rgba(244, 67, 54, 0.9)",
              },
            }}
            size="small"
          >
            <Close
              sx={{ fontSize: 14, color: theme?.palette?.common?.black }}
            />
          </IconButton>
          <CallEndedScreen />
        </Box>
      );
    } else if (currentpath.includes("/ehr/scheduling")) {
      return <FullScreenCallEndedScreen />;
    }
  }

  if (!showActiveCall) return null;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "fixed",
        top: isMinimized ? position.y : isDashboard ? 0 : "58px",
        left: isMinimized ? position.x : 0,
        width: isMinimized ? size.width : "100vw",
        height: isMinimized
          ? size.height
          : isDashboard
            ? "100vh"
            : "calc(100vh - 58px)",
        zIndex: isMinimized ? 9999 : 10000,
        backgroundColor: "#000",
        borderRadius: isMinimized ? "12px" : 0,
        overflow: "hidden",
        cursor: isDragging ? "grabbing" : "default",
        boxShadow: isMinimized
          ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)"
          : "none",
        backdropFilter: isMinimized ? "blur(20px)" : "none",
        border: isMinimized ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
        transition: isMinimized
          ? "none"
          : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      onMouseEnter={() => isMinimized && setIsHovered(true)}
      onMouseLeave={() => isMinimized && setIsHovered(false)}
    >
      {!isMinimized && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 10,
            display: "flex",
            gap: 1,
            p: 2,
          }}
        >
          <Tooltip title="End Call" arrow>
            <IconButton
              onClick={endCall}
              sx={{
                color: "white",
                backgroundColor: "rgba(244, 67, 54, 0.7)",
                backdropFilter: "blur(10px)",
                "&:hover": {
                  backgroundColor: "rgba(244, 67, 54, 0.9)",
                },
              }}
            >
              <Close />
            </IconButton>
          </Tooltip>
          {isCallJoined && !isMinimized && (
            <Tooltip title="Invite People" arrow>
              <IconButton
                onClick={() => setIsInviteModalOpen(true)}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                }}
              >
                <GroupAddIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Minimize" arrow>
            <IconButton
              onClick={minimizeJitsi}
              sx={{
                color: "white",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <FullscreenExitIcon />
            </IconButton>
          </Tooltip>

          {appointmentUuid && !isVisitNotePage && isCallJoined && (
            <Tooltip title="Start Visit Note" arrow>
              <Button
                onClick={() => {
                  if (occurrenceDate) {
                    dispatch(setOccurenceDate(occurrenceDate));
                  }
                  navigate(`/ehr/scheduling/visit-note/add/${appointmentUuid}`);
                  minimizeJitsi();
                }}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(33, 150, 243, 0.8)",
                  backdropFilter: "blur(10px)",
                  "&:hover": {
                    backgroundColor: "rgba(33, 150, 243, 1)",
                  },
                }}
              >
                Start Visit Note
              </Button>
            </Tooltip>
          )}
        </Box>
      )}

      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: isMinimized ? "12px" : 0,
          overflow: "hidden",
        }}
      >
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
            }}
          >
            <CircularProgress size={60} sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white", mt: 2 }}>
              Connecting to video call...
            </Typography>
          </Box>
        )}
        {jitsiJwt && (
          <JitsiMeeting
            domain={VITE_JITSI_URL}
            roomName={appointmentUuid || roomName}
            configOverwrite={jitsiConfig}
            interfaceConfigOverwrite={jitsiInterfaceConfig}
            userInfo={userInfo}
            jwt={jitsiJwt}
            onApiReady={(externalApi) => {
              externalApi.addEventListeners({
                participantJoined: () => {
                  setCallActive();
                },
                videoConferenceJoined: (e) => {
                  dispatch(setIsCallJoined(true));
                  setCallActive();
                },
                readyToClose: () => {
                  endCall();
                  dispatch(setIsCallJoined(false));
                },
              });
              // Force the participant's display name — Jitsi caches displayName
              // in its own localStorage and will otherwise ignore userInfo on
              // subsequent joins (e.g. if a previous session connected as
              // "Participant" before Redux hydrated).
              if (userInfo.displayName) {
                externalApi.executeCommand("displayName", userInfo.displayName);
              }
              externalApi.executeCommand(
                "subject",
                displayName || "Virtual Appointment",
              );
            }}
            onReadyToClose={() => {
              endCall();
            }}
            getIFrameRef={(iframe) => {
              if (iframe) {
                setIsLoading(false);
                iframe.style.border = "none";
                iframe.style.borderRadius = isMinimized ? "12px" : "0";
                iframe.style.width = "100%";
                iframe.style.height = "100%";
              }
            }}
          />
        )}
      </Box>
      {isMinimized && (
        <>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "60px",
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 50%, transparent 100%)",
              borderTopLeftRadius: "12px",
              borderTopRightRadius: "12px",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1,
              zIndex: 10,
              cursor: "grab",
              "&:active": {
                cursor: "grabbing",
              },
            }}
            onMouseDown={handleDragStart}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DragIndicatorIcon
                sx={{
                  color: "white",
                  fontSize: 16,
                  opacity: isHovered ? 1 : 0.7,
                  transition: "opacity 0.2s ease",
                }}
              />
              <VideocamIcon
                sx={{
                  fontSize: 18,
                  color: callState === "joining" ? "#FFA726" : "#4CAF50",
                  filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7))",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: "12px",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.7)",
                  maxWidth: "120px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {roomName}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {appointmentUuid && !isVisitNotePage && isCallJoined && (
                <Tooltip title="Start Visit Note" arrow>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      if (occurrenceDate) {
                        dispatch(setOccurenceDate(occurrenceDate));
                      }
                      minimizeJitsi();
                      navigate(
                        `/ehr/scheduling/visit-note/add/${appointmentUuid}`,
                      );
                    }}
                    sx={{
                      color: "white",
                      padding: "4px",
                      backgroundColor: "rgba(33, 150, 243, 0.8)",
                      backdropFilter: "blur(10px)",
                      "&:hover": {
                        backgroundColor: "rgba(33, 150, 243, 1)",
                      },
                    }}
                    size="small"
                  >
                    <NoteAddIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Maximize" arrow>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    maximizeJitsi();
                  }}
                  sx={{
                    color: "white",
                    padding: "4px",
                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                    backdropFilter: "blur(10px)",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                    },
                  }}
                  size="small"
                >
                  <FullscreenIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="End Call" arrow>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    endCall();
                  }}
                  sx={{
                    color: "white",
                    padding: "4px",
                    backgroundColor: "rgba(244, 67, 54, 0.7)",
                    backdropFilter: "blur(10px)",
                    "&:hover": {
                      backgroundColor: "rgba(244, 67, 54, 0.9)",
                    },
                  }}
                  size="small"
                >
                  <Close sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: "24px",
              height: "24px",
              cursor: "nwse-resize",
              zIndex: 20,
              opacity: isHovered ? 1 : 0.3,
              transition: "opacity 0.2s ease",
              background:
                "linear-gradient(135deg, transparent 50%, rgba(255, 255, 255, 0.3) 50%)",
              borderBottomRightRadius: "12px",
              touchAction: "none",
            }}
            onMouseDown={handleResizeStart}
            onTouchStart={(e) => {
              e.preventDefault();
              if (e.touches && e.touches[0]) {
                const touch = e.touches[0];
                handleResizeStart({
                  clientX: touch.clientX,
                  clientY: touch.clientY,
                  stopPropagation: () => {},
                  preventDefault: () => {},
                } as React.MouseEvent);
              }
            }}
          />
        </>
      )}
      {isInviteModalOpen && (
        <Invite
          isInviteModalOpen={isInviteModalOpen}
          setIsInviteModalOpen={setIsInviteModalOpen}
          callLink={callLink}
          appointmentUuid={appointmentUuid}
        />
      )}
    </Box>
  );
};

export default SharedVideoCall;
